import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { amo1cApi } from '../api/endpoints';
import KPICard from '../components/KPICard';
import DataTable from '../components/DataTable';
import LoadingWait from '../components/LoadingWait';

const CONF_LABEL = {
  phone: 'Телефон',
  email: 'Email',
  name_exact: 'Имя точно',
  name_fuzzy: 'Имя ≈',
};

const CONF_COLOR = {
  phone: 'var(--success)',
  email: 'var(--success)',
  name_exact: 'var(--warning)',
  name_fuzzy: 'var(--danger)',
};

const SEG_LABEL = {
  sna: 'SNA',
  plas_dist: 'PLAS дистр.',
  'sna+plas_dist': 'SNA+PLAS',
};

const PAGE = 100;

export default function Amo1c() {
  const [view, setView] = useState('matched');
  const [confidence, setConfidence] = useState('');
  const [q, setQ] = useState('');
  const [qDraft, setQDraft] = useState('');
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [selectedClient, setSelectedClient] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  const load = useCallback(async ({ forceRefresh = false } = {}) => {
    setError('');
    if (forceRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const r = await amo1cApi.match({
        view,
        limit: PAGE,
        offset,
        q: q || undefined,
        confidence: view === 'matched' && confidence ? confidence : undefined,
        force: forceRefresh || undefined,
      });
      const data = r.data;
      setSummary(data.summary || null);
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Ошибка загрузки');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [view, confidence, q, offset]);

  useEffect(() => {
    load();
  }, [load]);

  // сброс провала при смене списка
  useEffect(() => {
    setSelectedClient(null);
    setOrders([]);
  }, [view, confidence, q, offset]);

  const applySearch = () => {
    setOffset(0);
    setQ(qDraft.trim());
  };

  const onClientClick = async (row) => {
    const cid = row.client_id || row.primary_name;
    if (!cid) return;
    if (selectedClient?.id === cid) {
      setSelectedClient(null);
      setOrders([]);
      return;
    }
    setSelectedClient({
      id: cid,
      name: row.primary_name || row.client_id || cid,
    });
    setOrdersLoading(true);
    try {
      const r = await amo1cApi.clientOrders(cid);
      setOrders(r.data || []);
    } catch {
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  };

  const byConf = summary?.by_confidence || {};

  const columns = useMemo(() => {
    if (view === 'unmatched') {
      return [
        { key: 'client_id', label: 'Клиент 1С' },
        {
          key: 'segment',
          label: 'Сегмент',
          render: (v) => SEG_LABEL[v] || v || '—',
        },
        { key: 'revenue', label: 'Выручка', fmt: 'money' },
        { key: 'first_date', label: 'Первая покупка' },
        { key: 'last_date', label: 'Последняя' },
        {
          key: 'has_1c_phone',
          label: 'Тел. 1С',
          render: (v) => (v ? '●' : '—'),
        },
        {
          key: 'has_1c_email',
          label: 'Email 1С',
          render: (v) => (v ? '●' : '—'),
        },
        { key: 'lines', label: 'Строк' },
      ];
    }
    return [
      {
        key: 'primary_name',
        label: 'Клиент (1С) — главное',
        render: (v, row) => v || row.client_id || '—',
      },
      {
        key: 'segment',
        label: 'Сегмент',
        render: (v) => SEG_LABEL[v] || v || '—',
      },
      {
        key: 'confidence',
        label: 'Уверенность',
        render: (v) => (
          <span style={{ color: CONF_COLOR[v] || 'var(--text-secondary)', fontWeight: 600 }}>
            {CONF_LABEL[v] || v}
          </span>
        ),
      },
      {
        key: 'similarity',
        label: 'Сходство',
        render: (v, row) => {
          if (row.confidence === 'phone' || row.confidence === 'email') {
            return row.amo_name_differs ? 'имя в AMO другое' : '—';
          }
          if (row.confidence === 'name_fuzzy' && v != null) {
            return `${Math.round(Number(v) * 100)}%`;
          }
          return '—';
        },
      },
      {
        key: 'amo_name',
        label: 'В AMO как',
        render: (v, row) => {
          const company = row.amo_company;
          if (v && company && company !== v) return `${v} / ${company}`;
          return v || company || '—';
        },
      },
      {
        key: 'has_1c_phone',
        label: 'Тел. 1С',
        render: (v) => (v ? '●' : '—'),
      },
      {
        key: 'has_1c_email',
        label: 'Email 1С',
        render: (v) => (v ? '●' : '—'),
      },
      { key: 'revenue', label: 'Выручка 1С', fmt: 'money' },
    ];
  }, [view]);

  const pageFrom = total === 0 ? 0 : offset + 1;
  const pageTo = Math.min(offset + PAGE, total);
  const canPrev = offset > 0;
  const canNext = offset + PAGE < total;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>АМОИ1С</h1>
          <p style={styles.sub}>
            SNA (LIFT/Glow/Resaltys/ONYX/канюли) + дистры PLAS/БАД (позиция &gt; 10 шт).
            Приоритет: телефон → email → имя; главное имя — из 1С.
          </p>
        </div>
        <button
          type="button"
          style={styles.refreshBtn}
          disabled={refreshing || loading}
          onClick={() => load({ forceRefresh: true })}
        >
          {refreshing ? 'Считаем…' : 'Пересчитать'}
        </button>
      </div>

      {summary && (
        <div style={styles.kpiRow}>
          <KPICard title="Клиентов 1С" value={summary.clients_1c} />
          <KPICard title="Контактов AMO" value={summary.contacts_amo} />
          <KPICard title="Сопоставлено" value={summary.matched} suffix={` (${summary.match_rate}%)`} />
          <KPICard title="Без пары" value={summary.unmatched} />
          {summary.partners_1c?.clients_sna_only != null && (
            <KPICard title="SNA" value={summary.partners_1c.clients_sna_only} />
          )}
          {summary.partners_1c?.clients_plas_dist != null && (
            <KPICard title="PLAS дистры" value={summary.partners_1c.clients_plas_dist} />
          )}
          {summary.partners_1c?.clients_with_phone != null && (
            <KPICard title="С тел. из 1С" value={summary.partners_1c.clients_with_phone} />
          )}
          {summary.partners_1c?.clients_with_email != null && (
            <KPICard title="С email из 1С" value={summary.partners_1c.clients_with_email} />
          )}
        </div>
      )}

      {summary && (
        <div style={styles.confRow}>
          {[
            ['phone', 'Телефон'],
            ['email', 'Email'],
            ['name_exact', 'Имя точно'],
            ['name_fuzzy', 'Имя ≈'],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              style={{
                ...styles.confChip,
                borderColor: confidence === key ? CONF_COLOR[key] : 'var(--border)',
                color: CONF_COLOR[key],
                background: confidence === key ? `${CONF_COLOR[key]}18` : 'transparent',
              }}
              onClick={() => {
                setView('matched');
                setOffset(0);
                setConfidence((c) => (c === key ? '' : key));
              }}
            >
              {label}: {byConf[key] || 0}
            </button>
          ))}
        </div>
      )}

      {summary?.note && <p style={styles.note}>{summary.note}</p>}

      <div style={styles.toolbar}>
        <div style={styles.tabs}>
          {[
            { key: 'matched', label: 'Сопоставлено' },
            { key: 'unmatched', label: 'Без пары' },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              style={{
                ...styles.tab,
                ...(view === t.key ? styles.tabActive : {}),
              }}
              onClick={() => {
                setView(t.key);
                setOffset(0);
                if (t.key === 'unmatched') setConfidence('');
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div style={styles.searchWrap}>
          <input
            style={styles.input}
            placeholder="Поиск по имени…"
            value={qDraft}
            onChange={(e) => setQDraft(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applySearch()}
          />
          <button type="button" style={styles.searchBtn} onClick={applySearch}>
            Найти
          </button>
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}
      {loading ? (
        <LoadingWait />
      ) : (
        <>
          <p style={styles.hint}>Клик по клиенту — все заказы 1С за всё время</p>
          <DataTable
            columns={columns}
            rows={rows}
            onRowClick={onClientClick}
            activeRowKey={selectedClient?.id}
            rowKeyField="client_id"
          />

          {selectedClient && (
            <div style={styles.ordersBlock}>
              <div style={styles.ordersHead}>
                <h3 style={styles.ordersTitle}>
                  Заказы — {selectedClient.name}
                  {!ordersLoading && orders.length > 0
                    ? ` · ${orders.length.toLocaleString('ru-RU')} поз.`
                    : ''}
                </h3>
                <button
                  type="button"
                  style={styles.closeBtn}
                  onClick={() => { setSelectedClient(null); setOrders([]); }}
                >
                  Закрыть
                </button>
              </div>
              {ordersLoading ? (
                <LoadingWait />
              ) : orders.length === 0 ? (
                <div style={styles.emptyOrders}>Нет заказов в 1С</div>
              ) : (
                <DataTable
                  columns={[
                    { key: 'date', label: 'Дата' },
                    { key: 'order_number', label: 'Заказ' },
                    { key: 'sku', label: 'Номенклатура' },
                    { key: 'qty', label: 'Кол-во' },
                    { key: 'revenue', label: 'Выручка', fmt: 'money' },
                    { key: 'territory', label: 'Менеджер' },
                  ]}
                  rows={orders}
                  pageSize={50}
                />
              )}
            </div>
          )}

          <div style={styles.pager}>
            <span style={styles.pagerInfo}>
              {pageFrom}–{pageTo} из {total.toLocaleString('ru-RU')}
              {summary?.built_in_sec != null ? ` · построено за ${summary.built_in_sec} с` : ''}
            </span>
            <div style={styles.pagerBtns}>
              <button
                type="button"
                style={styles.pageBtn}
                disabled={!canPrev}
                onClick={() => setOffset(Math.max(0, offset - PAGE))}
              >
                ←
              </button>
              <button
                type="button"
                style={styles.pageBtn}
                disabled={!canNext}
                onClick={() => setOffset(offset + PAGE)}
              >
                →
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  page: { padding: '28px 32px 48px', maxWidth: 1400 },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
    marginBottom: 20,
  },
  title: {
    margin: 0,
    fontSize: 28,
    fontWeight: 700,
    color: 'var(--text-primary)',
    letterSpacing: '-0.03em',
    fontFamily: 'var(--font-display)',
  },
  sub: {
    margin: '8px 0 0',
    color: 'var(--text-secondary)',
    fontSize: 14,
    maxWidth: 560,
    lineHeight: 1.45,
  },
  refreshBtn: {
    background: 'var(--accent-dim)',
    color: 'var(--accent)',
    border: `1px solid rgba(34,211,238,.35)`,
    borderRadius: 10,
    padding: '10px 16px',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: 13,
  },
  kpiRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: 12,
    marginBottom: 14,
  },
  confRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 10,
  },
  confChip: {
    border: '1px solid',
    borderRadius: 999,
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    background: 'transparent',
  },
  note: {
    margin: '0 0 16px',
    fontSize: 12,
    color: 'var(--text-muted)',
    lineHeight: 1.4,
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 14,
    alignItems: 'center',
  },
  tabs: { display: 'flex', gap: 6 },
  tab: {
    background: 'transparent',
    border: '1px solid var(--border)',
    color: 'var(--text-secondary)',
    borderRadius: 8,
    padding: '8px 14px',
    cursor: 'pointer',
    fontSize: 13,
  },
  tabActive: {
    borderColor: 'var(--accent)',
    color: 'var(--accent)',
    background: 'var(--accent-dim)',
  },
  searchWrap: { display: 'flex', gap: 8 },
  input: {
    background: 'var(--bg-input)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    color: 'var(--text-primary)',
    padding: '8px 12px',
    minWidth: 200,
    fontSize: 13,
    outline: 'none',
  },
  searchBtn: {
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    color: 'var(--text-primary)',
    borderRadius: 8,
    padding: '8px 14px',
    cursor: 'pointer',
    fontSize: 13,
  },
  error: {
    color: 'var(--danger)',
    marginBottom: 12,
    fontSize: 13,
  },
  hint: {
    margin: '0 0 10px',
    fontSize: 12,
    color: 'var(--text-muted)',
  },
  ordersBlock: {
    marginTop: 20,
    padding: 16,
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    boxShadow: 'var(--card-rest)',
  },
  ordersHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  },
  ordersTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 650,
    color: 'var(--text-primary)',
  },
  closeBtn: {
    background: 'transparent',
    border: '1px solid var(--border)',
    color: 'var(--text-secondary)',
    borderRadius: 8,
    padding: '6px 12px',
    cursor: 'pointer',
    fontSize: 12,
  },
  emptyOrders: {
    color: 'var(--text-muted)',
    fontSize: 13,
    padding: '8px 0',
  },
  pager: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 12,
  },
  pagerInfo: { fontSize: 12, color: 'var(--text-muted)' },
  pagerBtns: { display: 'flex', gap: 6 },
  pageBtn: {
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    color: 'var(--text-primary)',
    borderRadius: 8,
    width: 36,
    height: 32,
    cursor: 'pointer',
  },
};
